import styles from './index.module.css'

import { LuX } from '@react-icons/all-files/lu/LuX'
import React, { useCallback, useState } from 'react'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'

import DocumentHeadTags from 'components/document-head-tags'
import {
  Badge,
  Button,
  Dialog,
  Flex,
  IconButton,
  RadioGroup,
  Separator,
  Text,
} from 'components/ds'

import { useDispatch } from 'hooks/use-redux'

const SORT_GROUPS = [
  {
    label: 'Position',
    options: [
      { value: 'custom', label: 'Manual order', isDefault: true },
      { value: 'tracknumber;asc', label: 'Track number: ascending' },
      { value: 'tracknumber;desc', label: 'Track number: descending' },
    ],
  },
  {
    label: 'Metadata',
    options: [
      { value: 'artist;asc', label: 'Artist: A–Z' },
      { value: 'track_title;asc', label: 'Track title: A–Z' },
    ],
  },
  {
    label: 'Date',
    options: [
      { value: 'date_added;desc', label: 'Date added: newest first' },
      { value: 'date_added;asc', label: 'Date added: oldest first' },
      { value: 'year;desc', label: 'Release year: newest first' },
      { value: 'year;asc', label: 'Release year: oldest first' },
    ],
  },
]

const ALL_OPTIONS = SORT_GROUPS.flatMap(g => g.options)

type Props = {
  currentSorting: string
  progressIndicator?: boolean
  onChange: (...args: Array<any>) => any
  onClose: (...args: Array<any>) => any
}

const TrackOrderModal = ({
  currentSorting = 'custom',
  progressIndicator,
  onChange,
  onClose,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const [sorting, setSorting] = useState<string>(currentSorting)

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      const option = ALL_OPTIONS.find(o => o.value === sorting)

      if (option) {
        dispatch(sendMixpanelEvent(`Change track order - ${option.label}`))
      }

      onChange(...sorting.split(';'))
    },
    [dispatch, sorting, onChange],
  )

  return (
    <Dialog.Root open onOpenChange={open => !open && onClose()}>
      <Dialog.Content maxWidth="480px">
        <DocumentHeadTags title="Change track order | DISCO" />
        <Flex justify="between" align="center" mt="2">
          <Dialog.Title mb="0" mt="0">
            Change track order
          </Dialog.Title>
          <IconButton variant="ghost" color="gray" size="2" onClick={onClose}>
            <LuX size={18} />
          </IconButton>
        </Flex>

        <form onSubmit={handleSubmit}>
          <RadioGroup.Root value={sorting} onValueChange={setSorting} mt="3">
            {SORT_GROUPS.map((group, groupIndex) => (
              <React.Fragment key={group.label}>
                {groupIndex > 0 && <Separator size="4" my="3" />}
                <Text
                  size="1"
                  weight="bold"
                  color="gray"
                  className={styles.groupLabel}
                >
                  {group.label.toUpperCase()}
                </Text>
                <Flex direction="column" mt="2">
                  {group.options.map(option => (
                    <label
                      key={option.value}
                      className={`${styles.optionRow} ${
                        sorting === option.value ? styles.optionRowSelected : ''
                      }`}
                    >
                      <RadioGroup.Item value={option.value} />
                      <Text size="3">{option.label}</Text>
                      {option.isDefault && (
                        <Badge variant="outline" ml="auto">
                          default
                        </Badge>
                      )}
                    </label>
                  ))}
                </Flex>
              </React.Fragment>
            ))}
          </RadioGroup.Root>

          <Flex gap="3" mt="5" justify="end">
            <Button type="button" variant="soft" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={progressIndicator}
              disabled={sorting === currentSorting}
            >
              Save
            </Button>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default TrackOrderModal
